import React, { useMemo, useState } from "react";
import Select from "./ui/Select";
import { validateProformaInvoiceItem } from "../utils/invoiceValidation";
import { toastErrors } from "../utils/toastErrors";
import { toLatinDigits } from "../utils/numberFormat";
import { PlusCircle, Trash2, Pencil } from "lucide-react";

// same select styles
const selectStyles = {
  control: (p) => ({
    ...p,
    fontFamily: "Vazirmatn, sans-serif",
    direction: "rtl",
    borderColor: "#E5E7EB",
    borderRadius: "0.75rem",
    paddingBlock: "2px",
    boxShadow: "none",
    backgroundColor: "#FFFFFF",
    minHeight: "42px",
    ":hover": { borderColor: "#2563EB" },
  }),
  placeholder: (p) => ({ ...p, color: "#9CA3AF", textAlign: "right" }),
  singleValue: (p) => ({ ...p, color: "#111827", textAlign: "right" }),
  multiValue: (p) => ({
    ...p,
    backgroundColor: "#EEF2FF",
    borderRadius: "0.5rem",
  }),
  multiValueLabel: (p) => ({ ...p, color: "#111827" }),
  multiValueRemove: (p) => ({
    ...p,
    ":hover": { backgroundColor: "#2563EB", color: "#fff" },
  }),
  menu: (p) => ({
    ...p,
    fontFamily: "Vazirmatn, sans-serif",
    direction: "rtl",
    borderRadius: "0.75rem",
    overflow: "hidden",
  }),
  option: (p, state) => ({
    ...p,
    textAlign: "right",
    backgroundColor: state.isSelected
      ? "#2563EB"
      : state.isFocused
      ? "#DBEAFE"
      : "#fff",
    color: state.isSelected ? "#fff" : "#111827",
  }),
};

const ProformaInvoiceItemsEditor = ({
  items,
  onItemsChange,
  unitOptions,
  countryOptions,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [draft, setDraft] = useState({});

  const totals = useMemo(() => {
    const sub = items.reduce(
      (a, it) =>
        a + (parseFloat(it.unit_price) || 0) * (parseFloat(it.quantity) || 0),
      0
    );
    const qty = items.reduce((a, it) => a + (parseFloat(it.quantity) || 0), 0);
    const gw = items.reduce((a, it) => a + (parseFloat(it.gw_kg) || 0), 0);
    const nw = items.reduce((a, it) => a + (parseFloat(it.nw_kg) || 0), 0);
    const pack = items.reduce((a, it) => a + (parseFloat(it.pack) || 0), 0);
    return { sub, qty, gw, nw, pack };
  }, [items]);

  const openModal = (index) => {
    setEditingIndex(index);
    setDraft({ ...items[index] });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingIndex(null);
    setDraft({});
  };

  const saveModal = () => {
    const problems = validateProformaInvoiceItem(draft);
    if (problems.length) {
      toastErrors(problems);
      return;
    }
    const updated = [...items];
    updated[editingIndex] = { ...draft };
    onItemsChange(updated);
    closeModal();
  };

  const removeItem = (index) => {
    const updated = [...items];
    updated.splice(index, 1);
    onItemsChange(updated);
  };

  const addItem = () => {
    onItemsChange([
      ...items,
      {
        translated_description: "",
        original_description: "",
        quantity: 1,
        unit_price: 0,
        unit: "PCS",
        commodity_code: "",
        nw_kg: 0,
        gw_kg: 0,
        pack: 0,
        origin: "",
      },
    ]);
  };

  return (
    <div dir="rtl" className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-base font-semibold">اقلام فاکتور</h4>
        <button
          type="button"
          onClick={addItem}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition"
        >
          <PlusCircle className="w-4 h-4" />
          افزودن کالا
        </button>
      </div>

      {/* Table */}
      {items?.length ? (
        <div className="overflow-x-auto rounded-xl border">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="px-3 py-2 text-right font-medium">شرح (EN)</th>
                <th className="px-3 py-2 text-right font-medium">شرح (FA)</th>
                <th className="px-3 py-2 text-right font-medium">تعداد</th>
                <th className="px-3 py-2 text-right font-medium">کد کالا</th>
                <th className="px-3 py-2 text-right font-medium">قیمت واحد</th>
                <th className="px-3 py-2 text-right font-medium">واحد</th>
                <th className="px-3 py-2 text-right font-medium">GW</th>
                <th className="px-3 py-2 text-right font-medium">NW</th>
                <th className="px-3 py-2 text-right font-medium">مبدأ</th>
                <th className="px-3 py-2 text-right font-medium">جمع خطی</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((it, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-3 py-2">{it.translated_description}</td>
                  <td className="px-3 py-2">{it.original_description}</td>
                  <td className="px-3 py-2">{it.quantity}</td>
                  <td className="px-3 py-2">{it.commodity_code}</td>
                  <td className="px-3 py-2">{it.unit_price}</td>
                  <td className="px-3 py-2">{it.unit}</td>
                  <td className="px-3 py-2">{it.gw_kg}</td>
                  <td className="px-3 py-2">{it.nw_kg}</td>
                  <td className="px-3 py-2">{it.origin}</td>
                  <td className="px-3 py-2">
                    {(
                      (parseFloat(it.unit_price) || 0) *
                      (parseFloat(it.quantity) || 0)
                    ).toFixed(2)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        type="button"
                        onClick={() => openModal(i)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-800 text-white hover:bg-gray-900"
                      >
                        <Pencil className="w-4 h-4" />
                        ویرایش
                      </button>
                      <button
                        type="button"
                        onClick={() => removeItem(i)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-600 text-white hover:bg-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                        حذف
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan={11} className="px-3 py-2">
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-700">
                    <span>
                      جمع فرعی: <b>{totals.sub.toFixed(2)}</b>
                    </span>
                    <span>
                      تعداد کل: <b>{totals.qty}</b>
                    </span>
                    <span>
                      NW: <b>{totals.nw}</b>
                    </span>
                    <span>
                      GW: <b>{totals.gw}</b>
                    </span>
                    <span>
                      Pack: <b>{totals.pack}</b>
                    </span>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="rounded-xl border p-4 text-gray-600">
          اقلامی برای نمایش وجود ندارد
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold mb-4">ویرایش کالا</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" dir="rtl">
              <div className="md:col-span-2 space-y-1">
                <label className="text-sm text-gray-600">شرح کالا لاتین</label>
                <textarea
                  className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  value={draft.translated_description || ""}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      translated_description: e.target.value,
                    }))
                  }
                  placeholder="شرح کالا (EN)"
                  required
                />
              </div>

              <div className="md:col-span-2 space-y-1">
                <label className="text-sm text-gray-600">شرح کالا فارسی</label>
                <textarea
                  className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  value={draft.original_description || ""}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      original_description: e.target.value,
                    }))
                  }
                  placeholder="شرح کالا (FA)"
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm text-gray-600">تعداد</label>
                <input
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  value={draft.quantity ?? 1}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, quantity: e.target.value }))
                  }
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm text-gray-600">قیمت واحد</label>
                <input
                  type="number"
                  step="0.01"
                  onWheel={(e) => e.target.blur()}
                  className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  value={draft.unit_price ?? 0}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, unit_price: e.target.value }))
                  }
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm text-gray-600">کد کالا (HS)</label>
                <input
                  className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  inputMode="numeric"
                  dir="ltr"
                  maxLength={9}
                  value={draft.commodity_code || ""}
                  onChange={(e) => {
                    const commodity_code = toLatinDigits(e.target.value).replace(/[^0-9]/g, "").slice(0, 9);
                    setDraft((d) => ({ ...d, commodity_code }));
                  }}
                  placeholder="حداکثر ۹ رقم"
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm text-gray-600">واحد</label>
                <Select
                  styles={selectStyles}
                  options={unitOptions}
                  value={
                    unitOptions.find((o) => o.value === draft.unit) || null
                  }
                  onChange={(opt) =>
                    setDraft((d) => ({ ...d, unit: opt ? opt.value : "" }))
                  }
                  placeholder="انتخاب واحد"
                  isClearable
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm text-gray-600">وزن خالص (NW)</label>
                <input
                  type="number"
                  step="0.01"
                  onWheel={(e) => e.target.blur()}
                  className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  value={draft.nw_kg ?? 0}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, nw_kg: e.target.value }))
                  }
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm text-gray-600">وزن ناخالص (GW)</label>
                <input
                  type="number"
                  step="0.01"
                  onWheel={(e) => e.target.blur()}
                  className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  value={draft.gw_kg ?? 0}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, gw_kg: e.target.value }))
                  }
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm text-gray-600">تعداد بسته</label>
                <input
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  value={draft.pack ?? 0}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, pack: e.target.value }))
                  }
                />
              </div>

              <div className="md:col-span-2 space-y-1">
                <label className="text-sm text-gray-600">مبدأ</label>
                <Select
                  isMulti
                  styles={selectStyles}
                  options={countryOptions}
                  value={
                    draft.origin
                      ? draft.origin
                          .split("-")
                          .map((v) =>
                            countryOptions.find((o) => o.value === v.trim())
                          )
                      : []
                  }
                  onChange={(opts) =>
                    setDraft((d) => ({
                      ...d,
                      origin: (opts || []).map((o) => o.value).join("-"),
                    }))
                  }
                  placeholder="انتخاب مبدأ"
                />
              </div>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex items-center px-4 py-2 rounded-xl border bg-white hover:bg-gray-50"
              >
                انصراف
              </button>
              <button
                type="button"
                onClick={saveModal}
                className="inline-flex items-center px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700"
              >
                ذخیره
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProformaInvoiceItemsEditor;
